/**
 * Admin · API error console (GET /admin/api-errors, cursor-paged). The middleware masks every
 * unhandled exception to a generic {error:"internal_error"} response — this screen surfaces the
 * real message/stack/route it captured (api_errors, migration 0072), so a platform admin can see
 * what actually threw without Azure Portal/CLI log access.
 */
import { Fragment, useState } from "react";
import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api.js";

interface ApiError {
  id: string;
  route: string;
  message: string;
  stack: string | null;
  orgId: string | null;
  userId: string | null;
  invocationId: string | null;
  createdAt: string;
}

interface ApiErrorsResponse {
  entries: ApiError[];
  nextBefore: string | null;
}

export function ApiErrors(): ReactNode {
  const [route, setRoute] = useState("");
  // A stack of `before` cursors: empty = newest page; push to go older, pop to come back.
  const [cursors, setCursors] = useState<string[]>([]);
  const before = cursors.length > 0 ? cursors[cursors.length - 1] : null;
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin-api-errors", route, before],
    queryFn: () =>
      api.get<ApiErrorsResponse>(
        `/admin/api-errors?limit=50${route ? `&route=${encodeURIComponent(route)}` : ""}${
          before ? `&before=${encodeURIComponent(before)}` : ""
        }`,
      ),
  });
  const entries = data?.entries ?? [];

  const changeRoute = (next: string): void => {
    setRoute(next);
    setCursors([]);
  };

  return (
    <section>
      <div className="page-h">
        <div>
          <h1>API errors</h1>
          <div className="sub">
            Real message/stack behind a masked 500 — the middleware writes one row here per
            unhandled exception · live from the API
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <input
            type="text"
            placeholder="Filter by route (e.g. requisitions:create)"
            value={route}
            onChange={(e) => changeRoute(e.target.value)}
            style={{ minWidth: 240 }}
          />
          <span className="pill n">Platform</span>
        </div>
      </div>

      {isLoading && <div className="card pad mut sm">Loading API errors…</div>}

      {isError && (
        <div className="banner crit">
          <span>Couldn't load API errors. Please retry in a moment.</span>
        </div>
      )}

      {!isLoading && !isError && entries.length === 0 && (
        <div className="card pad">
          <p className="mut sm" style={{ margin: 0 }}>
            {route
              ? `No errors recorded for route "${route}".`
              : "Nothing here — no unhandled exceptions have been recorded yet."}
          </p>
        </div>
      )}

      {!isLoading && !isError && entries.length > 0 && (
        <div className="card">
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Occurred</th>
                  <th>Route</th>
                  <th>Message</th>
                  <th>Org / User</th>
                  <th>Invocation</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => {
                  const expanded = expandedId === e.id;
                  return (
                    <Fragment key={e.id}>
                      <tr
                        onClick={() => setExpandedId(expanded ? null : e.id)}
                        style={{ cursor: "pointer" }}
                      >
                        <td className="mut sm tnum" style={{ whiteSpace: "nowrap" }}>
                          {new Date(e.createdAt).toLocaleString()}
                        </td>
                        <td style={{ fontWeight: 600 }}>{e.route}</td>
                        <td className="mut sm" style={{ maxWidth: 420 }}>
                          <span title={e.message}>{e.message}</span>
                        </td>
                        <td className="mut sm tnum">
                          {e.orgId ? `${e.orgId.slice(0, 8)}…` : "—"}
                          {e.userId ? ` / ${e.userId.slice(0, 8)}…` : ""}
                        </td>
                        <td className="mut sm tnum">{e.invocationId ?? "—"}</td>
                      </tr>
                      {expanded && (
                        <tr>
                          <td colSpan={5}>
                            <pre
                              className="mut sm"
                              style={{
                                whiteSpace: "pre-wrap",
                                margin: 0,
                                maxHeight: 320,
                                overflowY: "auto",
                              }}
                            >
                              {e.stack ?? "(no stack captured)"}
                            </pre>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
        <button
          className="btn sm ghost"
          disabled={cursors.length === 0}
          onClick={() => setCursors((c) => c.slice(0, -1))}
        >
          ← Newer
        </button>
        <button
          className="btn sm ghost"
          disabled={!data?.nextBefore}
          onClick={() => {
            const next = data?.nextBefore;
            if (next) setCursors((c) => [...c, next]);
          }}
        >
          Older →
        </button>
      </div>
    </section>
  );
}
